// GOOP ZONE — Quests Screen (LIVE VERSION)
// Real progress from lifetime stats. Unlocks percs + characters automatically.

import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { PEANUT_PERCS } from '../game/peanutPercs';
import type { GoopSystemState, LifetimeStats } from '../game/goopSystem';

interface QuestsScreenProps {
  onClose: () => void;
  goopState?: GoopSystemState;
}

type Tab = 'perks' | 'chars' | 'stats';

// ── Chalk palette ─────────────────────────────────────────────────────────────
const C = {
  white:     'rgba(224, 230, 241, 0.92)',
  bright:    'rgba(242, 246, 252, 1.0)',
  dim:       'rgba(152, 162, 180, 0.60)',
  highlight: 'rgba(255, 170, 80, 0.95)',
  board:     'rgba(15, 17, 23, 0.0)',   // full-bleed now — surface is the backdrop
  border:    'rgba(125, 138, 162, 0.28)',
  done:      'rgba(255, 150, 60, 0.90)',
  goop:      'rgba(255, 207, 77, 0.95)',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function getStatForCondition(conditionType: string, stats: LifetimeStats): number {
  switch (conditionType) {
    case 'total_kills':     return stats.totalKills;
    case 'total_gold':      return stats.totalGoldCollected;
    case 'wave_reached':    return stats.highestWaveReached;
    case 'total_deaths':    return stats.totalDeaths;
    case 'survive_minutes': return stats.longestSurvivalMinutes;
    case 'level_reached':   return stats.highestLevelReached;
    case 'boss_killed':     return stats.bossesKilled;
    case 'total_chests':    return stats.totalChestsOpened;
    default:                return 0;
  }
}

function getUnit(conditionType: string): string {
  switch (conditionType) {
    case 'total_kills':     return 'kills';
    case 'total_gold':      return 'gold';
    case 'wave_reached':    return 'waves';
    case 'total_deaths':    return 'deaths';
    case 'survive_minutes': return 'min';
    case 'level_reached':   return 'lv';
    case 'boss_killed':     return 'bosses';
    case 'total_chests':    return 'chests';
    default:                return '';
  }
}

/** Compact number formatter: 1500 → "1.5k", 500 → "500" */
function fmt(n: number): string {
  const v = Math.round(n * 10) / 10;
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(Math.round(v));
}

/** Format minutes as "3m 42s" */
function fmtMin(minutes: number): string {
  const m = Math.floor(minutes);
  const s = Math.round((minutes - m) * 60);
  return `${m}m ${s}s`;
}

/** Format total seconds as "1h 24m" or "18m" */
function fmtTotalTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── Character unlock quest definitions ───────────────────────────────────────

interface CharQuest {
  id: string;
  charName: string;
  conditionLabel: string;
  statKey: keyof LifetimeStats;
  goal: number;
  unit: string;
}

const CHARACTER_QUESTS: CharQuest[] = [
  { id: 'char_killa',  charName: 'Duck',            conditionLabel: 'Kill 250 enemies total',         statKey: 'totalKills',            goal: 250,  unit: 'kills'  },
  { id: 'char_silly',  charName: 'Gen-Z Coolkid',        conditionLabel: 'Complete 5 runs',                statKey: 'totalRunsPlayed',       goal: 5,    unit: 'runs'   },
  { id: 'char_wizard', charName: 'Robot Girl',           conditionLabel: 'Survive 10 min in a single run', statKey: 'longestSurvivalMinutes', goal: 10,   unit: 'min'    },
  { id: 'char_chad',   charName: 'Buff Guy',             conditionLabel: 'Collect 2,500 gold total',       statKey: 'totalGoldCollected',    goal: 2500, unit: 'gold'   },
  { id: 'char_lebron', charName: 'Smokeshow', conditionLabel: 'Reach level 15 in a single run', statKey: 'highestLevelReached',   goal: 15,   unit: 'lv'     },
  { id: 'char_pirate', charName: 'Pirate',           conditionLabel: 'Open 10 chests total',           statKey: 'totalChestsOpened',     goal: 10,   unit: 'chests' },
  { id: 'char_viking', charName: 'Viking',           conditionLabel: 'Kill 3 bosses total',            statKey: 'bossesKilled',          goal: 3,    unit: 'bosses' },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function QuestsScreen({ onClose, goopState }: QuestsScreenProps) {
  const [activeTab, setActiveTab] = useState<Tab>('perks');
  const stats = goopState?.lifetimeStats;
  const hasRuns = (stats?.totalRunsPlayed ?? 0) > 0;

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); }
    },
    [onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  // BETA 16.95 — Controller phase 3 cont: B button on gamepad closes
  // the Quests subpage. Same pattern as the Settings overlay (16.93).
  // Per-quest scroll-with-dpad deferred — quests list is read-only,
  // close-and-go is the main interaction.
  useEffect(() => {
    let cancelled = false;
    const setup = async () => {
      const focusMod = await import('../game/gamepadFocus');
      if (cancelled) return;
      focusMod.registerFocusScreen('quests_overlay', [], () => onClose(), 0);
      focusMod.setActiveScreen('quests_overlay');
    };
    setup();
    return () => {
      cancelled = true;
      import('../game/gamepadFocus').then(focusMod => {
        focusMod.unregisterFocusScreen('quests_overlay');
        if (focusMod.getActiveScreen() === 'quests_overlay') focusMod.setActiveScreen(null);
      });
    };
  }, [onClose]);

  // Build perk quest rows from PEANUT_PERCS (non-default unlock conditions)
  const percRows = PEANUT_PERCS
    .filter(p => p.unlockCondition.type !== 'default')
    .map(p => {
      const current = stats ? getStatForCondition(p.unlockCondition.type, stats) : 0;
      const isUnlocked = goopState?.unlockedPercIds.has(p.id) ?? false;
      return {
        id: p.id,
        title: p.name,
        description: p.unlockCondition.description,
        reward: p.description,
        current: Math.min(current, p.unlockCondition.value),
        goal: p.unlockCondition.value,
        unit: getUnit(p.unlockCondition.type),
        done: isUnlocked,
      };
    });

  // Sort: incomplete first (by progress %), then completed
  percRows.sort((a, b) => {
    if (a.done && !b.done) return 1;
    if (!a.done && b.done) return -1;
    const pctA = a.current / a.goal;
    const pctB = b.current / b.goal;
    return pctB - pctA; // closer to completion first
  });

  // Build character quest rows.
  // BETA 19.17 — BUG #6 from local audit. Previously `done` was computed
  // ONLY from the lifetime-stat threshold. That ignored the actual
  // unlock state, so DevPeanut + bot-mode users (who have every
  // character in `unlockedCharacterIds` from applyDevUserUnlocks /
  // activateAllForBotMode) still saw character quests as locked when
  // their stat-bump didn't cover that quest's tracked stat
  // (e.g. longestSurvivalMinutes, totalGoldCollected, totalChestsOpened,
  // highestLevelReached — applyDevUserUnlocks only bumps kills/deaths/
  // bosses/runs). Now: done is true if EITHER the character is in the
  // unlocked set OR the stat threshold is met.
  const charRows = CHARACTER_QUESTS.map(q => {
    const charId = q.id.replace(/^char_/, '');
    const isUnlocked = goopState?.unlockedCharacterIds?.has(charId) ?? false;
    const currentStat = (stats?.[q.statKey] as number) ?? 0;
    return {
      ...q,
      current: Math.min(currentStat, q.goal),
      done: isUnlocked || currentStat >= q.goal,
    };
  });

  charRows.sort((a, b) => {
    if (a.done && !b.done) return 1;
    if (!a.done && b.done) return -1;
    const pctA = a.current / a.goal;
    const pctB = b.current / b.goal;
    return pctB - pctA;
  });

  const tabs: { id: Tab; label: string }[] = [
    { id: 'perks',  label: 'PERK UNLOCKS' },
    { id: 'chars',  label: 'CHARACTERS'   },
    { id: 'stats',  label: 'MY STATS'     },
  ];

  const percDone  = percRows.filter(r => r.done).length;
  const charDone  = charRows.filter(r => r.done).length;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      {/* Dark backdrop */}
      <div className="absolute inset-0" style={{ background:
        'radial-gradient(ellipse 85% 60% at 50% 112%, rgba(255,110,20,0.14) 0%, transparent 60%),' +
        'radial-gradient(ellipse 50% 40% at 8% -5%, rgba(150,70,220,0.10), transparent 60%),' +
        'radial-gradient(ellipse 50% 40% at 94% -5%, rgba(150,70,220,0.08), transparent 60%),' +
        'linear-gradient(180deg, #16181f 0%, #101218 45%, #0a0b10 100%)' }} />
      <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.55\'/%3E%3C/svg%3E")', opacity: 0.08, mixBlendMode: 'overlay', pointerEvents: 'none' }} />

      {/* Panel */}
      <motion.div
        className="relative z-10 w-full h-full"
        style={{ padding: '3.2% 5% 2.4%' }}
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      >
        <div
          style={{
            background: 'transparent',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            height: '100%',
            fontFamily: "'Share Tech Mono', 'Courier New', monospace",
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 20px 12px',
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
              <span
                style={{
                  fontFamily: "'Anton', 'Archivo Black', sans-serif",
                  color: C.bright,
                  fontSize: '2.4rem',
                  letterSpacing: '0.25em',
                  textShadow: '1px 1px 0 rgba(0,0,0,0.8)',
                }}
              >
                PERFORMANCE REVIEW
              </span>
              {goopState && (
                <span style={{ color: C.goop, fontSize: '0.73rem', letterSpacing: '0.12em' }}>
                  GOOP: {fmt(goopState.goop)}
                </span>
              )}
            </div>
            <ChalkButton onClick={onClose} label="[ESC] CLOSE" small />
          </div>

          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.67rem',
                  letterSpacing: '0.13em',
                  padding: '9px 6px',
                  color: activeTab === tab.id ? C.bright : C.dim,
                  background: activeTab === tab.id ? 'rgba(222,229,240,0.07)' : 'transparent',
                  borderBottom: activeTab === tab.id ? `2px solid rgba(222,229,240,0.5)` : '2px solid transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.1s',
                  position: 'relative',
                }}
              >
                {tab.label}
                {/* Progress badge */}
                {tab.id === 'perks' && (
                  <span style={{ marginLeft: 6, color: percDone === percRows.length ? C.done : C.dim, fontSize: '0.62rem' }}>
                    {percDone}/{percRows.length}
                  </span>
                )}
                {tab.id === 'chars' && (
                  <span style={{ marginLeft: 6, color: charDone === charRows.length ? C.done : C.dim, fontSize: '0.62rem' }}>
                    {charDone}/{charRows.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content area */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '14px 20px' }}>

            {/* ── PERK UNLOCKS tab ── */}
            {activeTab === 'perks' && (
              <>
                <div style={{ color: C.dim, fontSize: '0.67rem', letterSpacing: '0.10em', marginBottom: 14 }}>
                  HIT THESE MILESTONES TO UNLOCK PERCS
                </div>
                {!hasRuns && (
                  <EmptyState text="Play your first run to start tracking kills and progress!" />
                )}
                {percRows.map((row, i) => (
                  <QuestRow
                    key={row.id}
                    title={row.title}
                    description={row.description}
                    reward={row.reward}
                    current={row.current}
                    goal={row.goal}
                    unit={row.unit}
                    done={row.done}
                    isLast={i === percRows.length - 1}
                  />
                ))}
              </>
            )}

            {/* ── CHARACTERS tab ── */}
            {activeTab === 'chars' && (
              <>
                <div style={{ color: C.dim, fontSize: '0.67rem', letterSpacing: '0.10em', marginBottom: 14 }}>
                  UNLOCK PLAYABLE CHARACTERS — COWBOY & MILITARY GUY START UNLOCKED
                </div>
                {!hasRuns && (
                  <EmptyState text="Play your first run to start tracking progress toward character unlocks!" />
                )}
                {/* Always-unlocked starters */}
                <div
                  style={{
                    marginBottom: 12,
                    padding: '8px 12px',
                    border: `1px solid rgba(255,150,60,0.25)`,
                    borderRadius: 2,
                    background: 'rgba(255,150,60,0.05)',
                  }}
                >
                  <span style={{ color: C.done, fontSize: '0.76rem', letterSpacing: '0.12em' }}>
                    ✓ COWBOY &amp; MILITARY GUY — UNLOCKED FROM THE START
                  </span>
                </div>
                {charRows.map((row, i) => (
                  <QuestRow
                    key={row.id}
                    title={row.charName}
                    description={row.conditionLabel}
                    reward="PLAYABLE CHARACTER"
                    current={row.current}
                    goal={row.goal}
                    unit={row.unit}
                    done={row.done}
                    isLast={i === charRows.length - 1}
                  />
                ))}
              </>
            )}

            {/* ── MY STATS tab ── */}
            {activeTab === 'stats' && (
              <>
                {!hasRuns && (
                  <EmptyState text="No runs yet. Get out there and start blasting!" />
                )}
                {stats && hasRuns && (
                  <StatsGrid stats={stats} goop={goopState?.goop ?? 0} />
                )}
              </>
            )}

          </div>

          {/* Footer */}
          <div
            style={{
              padding: '12px 20px',
              borderTop: `1px solid ${C.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              flexShrink: 0,
            }}
          >
            <ChalkButton onClick={onClose} label="CLOSE" />
          </div>

        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function EmptyState({ text }: { text: string }) {
  return (
    <div style={{ color: 'rgba(152,162,180,0.42)', fontSize: '0.76rem', textAlign: 'center', padding: '28px 0 16px', letterSpacing: '0.08em', lineHeight: 1.7 }}>
      {text}
    </div>
  );
}

function QuestRow({
  title, description, reward, current, goal, unit, done, isLast,
}: {
  title: string;
  description: string;
  reward: string;
  current: number;
  goal: number;
  unit: string;
  done: boolean;
  isLast: boolean;
}) {
  const pct = Math.min(100, Math.round((current / goal) * 100));

  // Format the progress numbers
  const showCurrent = unit === 'min' ? fmtMin(current) : fmt(current);
  const showGoal    = unit === 'min' ? fmtMin(goal)    : fmt(goal);

  return (
    <div
      style={{
        borderBottom: isLast ? 'none' : `1px solid rgba(125,138,162,0.15)`,
        paddingBottom: 14,
        marginBottom: 14,
        opacity: done ? 0.80 : 1,
      }}
    >
      {/* Title row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span
          style={{
            color: done ? C.done : C.highlight,
            fontSize: '0.85rem',
            letterSpacing: '0.12em',
            fontFamily: "'Anton', 'Archivo Black', sans-serif",
          }}
        >
          {done ? '✓ ' : ''}{title}
        </span>
        <span style={{ color: done ? C.done : C.dim, fontSize: '0.64rem', letterSpacing: '0.07em' }}>
          {done ? 'UNLOCKED' : `+${reward}`}
        </span>
      </div>

      {/* Description */}
      <div style={{ color: C.dim, fontSize: '0.72rem', marginBottom: done ? 0 : 8, letterSpacing: '0.05em', lineHeight: 1.5 }}>
        {description}
      </div>

      {/* Progress bar — hidden when complete */}
      {!done && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              flex: 1,
              height: 5,
              background: 'rgba(125,138,162,0.10)',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                background: pct >= 80 ? 'rgba(255,150,60,0.85)' : pct >= 40 ? 'rgba(255,175,85,0.70)' : 'rgba(222,229,240,0.50)',
                borderRadius: 1,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
          <span style={{ color: C.dim, fontSize: '0.65rem', minWidth: 90, textAlign: 'right', letterSpacing: '0.04em' }}>
            {showCurrent} / {showGoal} {unit}
          </span>
        </div>
      )}
    </div>
  );
}

function StatsGrid({ stats, goop }: { stats: LifetimeStats; goop: number }) {
  const items: { label: string; value: string }[] = [
    { label: 'RUNS PLAYED',    value: String(stats.totalRunsPlayed) },
    { label: 'TOTAL KILLS',    value: fmt(stats.totalKills) },
    { label: 'TOTAL DEATHS',   value: String(stats.totalDeaths) },
    { label: 'BEST WAVE',      value: String(stats.highestWaveReached) },
    { label: 'BEST LEVEL',     value: String(stats.highestLevelReached) },
    { label: 'LONGEST RUN',    value: fmtMin(stats.longestSurvivalMinutes) },
    { label: 'BOSSES KILLED',  value: String(stats.bossesKilled) },
    { label: 'GOLD COLLECTED', value: fmt(stats.totalGoldCollected) },
    { label: 'CHESTS OPENED',  value: String(stats.totalChestsOpened) },
    { label: 'GOOP BALANCE',   value: fmt(goop) },
    { label: 'GOOP LIFETIME',  value: fmt(stats.totalGoopCollected) },
    { label: 'TIME PLAYED',    value: fmtTotalTime(stats.totalTimePlayed) },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 32px', paddingTop: 4 }}>
      {items.map(item => (
        <div
          key={item.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: '1px solid rgba(125,138,162,0.10)',
            paddingBottom: 9,
          }}
        >
          <span style={{ color: C.dim, fontSize: '0.67rem', letterSpacing: '0.10em' }}>{item.label}</span>
          <span style={{ color: C.highlight, fontSize: '0.82rem', letterSpacing: '0.07em' }}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function ChalkButton({ onClick, label, small }: { onClick: () => void; label: string; small?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontFamily: "'Share Tech Mono', monospace",
        fontSize: small ? '0.72rem' : '0.82rem',
        letterSpacing: '0.15em',
        color: hov ? 'rgba(255,175,85,1.0)' : 'rgba(222,229,240,0.75)',
        background: hov ? 'rgba(222,229,240,0.10)' : 'transparent',
        border: `1px solid ${hov ? 'rgba(222,229,240,0.55)' : 'rgba(125,138,162,0.30)'}`,
        borderRadius: 2,
        padding: small ? '4px 10px' : '7px 20px',
        cursor: 'pointer',
        transition: 'all 0.12s',
        textShadow: hov ? '0 0 8px rgba(255,175,85,0.4)' : 'none',
      }}
    >
      {label}
    </button>
  );
}

function ChalkCorners() {
  const style = (pos: CSSProperties): CSSProperties => ({
    position: 'absolute',
    width: 12,
    height: 12,
    borderColor: 'rgba(125,138,162,0.50)',
    borderStyle: 'solid',
    ...pos,
  });
  return (
    <>
      <div style={style({ top: -1, left: -1, borderWidth: '2px 0 0 2px' })} />
      <div style={style({ top: -1, right: -1, borderWidth: '2px 2px 0 0' })} />
      <div style={style({ bottom: -1, left: -1, borderWidth: '0 0 2px 2px' })} />
      <div style={style({ bottom: -1, right: -1, borderWidth: '0 2px 2px 0' })} />
    </>
  );
}
